import {useState, useRef} from "react";
import {Button, Card, Row, Col, Table} from "antd";
import {getBatchReport} from "../../api/Api";
import {BatchSelectWidget} from "../inventory/BatchSelectWidget";
import {Link} from "react-router";
import {getColumnSearchProps} from "../widget/SearchProps";
import {fixedToDisplayUnit} from "../../util/unitutils";
import {CategoryType, UnitType} from "../../api/Constants";
import {toFSDate, toFSDateTime} from "../../util/timeutils";
import {saveAs} from 'file-saver';

const getPrice = (record) => {
    const {linePrice, lineDiscount} = record;
    const price = parseFloat(linePrice) - parseFloat(lineDiscount);
    return price;
}

const getCost = (record) => {
    if (record.category === CategoryType.Wholesale) {
        const {bulkCost, quantity, unit} = record;
        const displayQuantity = fixedToDisplayUnit(quantity, unit);
        return parseFloat(bulkCost) * displayQuantity;
    } else {
        const {startQuantity, cost, quantity} = record;
        const unitCost = parseFloat(cost) / startQuantity;
        const totalCost = unitCost * quantity;
        return totalCost;
    }
}

export const BatchReport = ({refresh, onRefresh, onApiFail, roomMap}) => {
    const [batch, setBatch] = useState(undefined);

    const [report, setReport] = useState([]);
    const [numReports, setNumReports] = useState(0);


    const runReport = () => {
        if (batch === undefined) {
            return;
        }

        getBatchReport(batch.id).then(result => {
            setReport(result.map((line, index) => {
                return {
                    ...line,
                    key: index,
                }
            }))
            setNumReports(numReports+1);
        }).catch(e => {
            // catch error
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    // const rowStyle = {
    //     margin: '10px'
    // }

    const colStyle = {
        marginTop: '5px',
        marginBottom: '5px'
    }

    const onBatchSelect = (batch) => {
        setBatch(batch);
    }

    const handleCSV = () => {
        const now = new Date();
        const lineData = ["Invoice, Name, Quantity, Unit, Timestamp, Category, Price, Cost, Profit, invoiceId, batchId, Batch Name, creationTime, batchStartTime, batchHarvestTime, Flower Room, Harvest Room\n"];
        const {name: batchName, id: batchId, created, startTime, harvestTime, flowerRoomId, harvestRoomId} = batch;

        const creationTime = (created) ? toFSDate(new Date(created)) : 'None';
        const batchStartTime = (startTime) ? toFSDate(new Date(startTime)) : 'None';
        const batchHarvestTime = (harvestTime) ? toFSDate(new Date(harvestTime)) : 'None';
        const flowerRoom = (flowerRoomId) ? roomMap[flowerRoomId].name : 'None';
        const harvestRoom = (harvestRoomId) ? roomMap[harvestRoomId].name : 'None';

        report.forEach(l => {
            const {name, timestamp, invoiceNumber, invoiceId, category, quantity, unit} = l;
            const itemTimestamp = toFSDate(new Date(timestamp));
            const itemCategory = CategoryType.Label[category];
            const itemUnit = UnitType.Label[unit];
            const itemQuantity = fixedToDisplayUnit(quantity, unit);
            const p = getPrice(l);
            const itemPrice = p.toFixed(2);
            const c = getCost(l);
            const itemCost = c.toFixed(2);
            const itemProfit = (p - c).toFixed(2);

            const lineBuffer = `${invoiceNumber}, ${name}, ${itemQuantity}, ${itemUnit}, ${itemTimestamp}, ${itemCategory}, ${itemPrice}, ${itemCost}, ${itemProfit}, ${invoiceId}, ${batchId}, ${batchName}, ${creationTime}, ${batchStartTime}, ${batchHarvestTime}, ${flowerRoom}, ${harvestRoom} \n`

            lineData.push(lineBuffer);
        });

        const file = new Blob(lineData, { type: 'text/plain;charset=utf-8' });

        const filename = `batch_${batchName}_${toFSDateTime(now)}.csv`
        saveAs(file, filename);
        console.log(`saved ${filename}`)
    }

    const cardStyle = {textAlign: 'left', margin: "5px"}

    const reportTitle = <Row>
        <Col span={2}>
            Batch Report
        </Col>
        <Col style={{color: 'red'}}>
            Data includes all "Wholesale" products and "Packaged Products" from this Batch
        </Col>
    </Row>

    const searchInput = useRef(null);

    const columns = [
        {
            title: 'Invoice',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            render: (val, record) => {
                return <Link to={`/invoice/${record.invoiceId}`}>{val}</Link>
            },
            ...getColumnSearchProps(`invoiceNumber`, searchInput),
            sorter: (a, b) => a.invoiceNumber.toLowerCase().localeCompare(b.invoiceNumber.toLowerCase()),
        },
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (val, record) => {
                return toFSDate(new Date(val));
            },
            sorter: (a, b) => (new Date(a.timestamp))- (new Date(b.timestamp)),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (val, record) => {
                const {inventoryId} = record;
                return <Link to={`/inventory/${inventoryId}`}>{val}</Link>
            },
            ...getColumnSearchProps(`name`, searchInput),
            sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (val, record) => {
                return CategoryType.Label[val];
            }
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (val, record) => {
                const {unit} = record;
                const displayQuantity = fixedToDisplayUnit(val, unit);
                return `${displayQuantity} ${UnitType.Label[unit]}`
            }
        },
        {
            title: 'Price',
            dataIndex: 'linePrice',
            key: 'price',
            render: (val, record, index) => {
                const price = getPrice(record);
                return `$ ${price.toFixed(2)}`;
            }
        },
        {
            title: 'Cost',
            dataIndex: 'linePrice',
            key: 'cost',
            render: (val, record, index) => {
                const cost = getCost(record);
                return `$ ${cost.toFixed(2)}`
            }
        },
        {
            title: 'Profit',
            dataIndex: 'linePrice',
            key: 'profit',
            render: (val, record) => {
                const price = getPrice(record);
                const cost = getCost(record);
                const profit = price - cost;
                let color = 'black';
                if (profit > 0) {
                    color = 'green'
                } else if (profit < 0) {
                    color = 'red'
                }
                return <div style={{color}}>
                    {`$ ${profit.toFixed(2)}`}
                </div>
            }
        }
    ];

    const tablePagination = {
        defaultPageSize: 100,
        showSizeChanger: true,
        pageSizeOptions: [100, 200, 300, 400]
    }

    const rowStyle = {
        marginTop: '5px',
        marginBottom: '5px',
    }

    return <div>
        <Card style={cardStyle} title={"Run Batch Report"}>
            <div style={colStyle}>
                <BatchSelectWidget selectedBatch={batch} onSelect={onBatchSelect} refresh={refresh} onApiFail={onApiFail} displayType={"inline"} />
            </div>
            <Button onClick={runReport}>Run</Button>
            <Row style={rowStyle}>
                <Button disabled={report.length <= 0} onClick={handleCSV}>Save CSV</Button>
            </Row>
        </Card>
        {(numReports > 0) && (report.length > 0) && <Card style={cardStyle} title={reportTitle}>
            <Table dataSource={report} columns={columns} pagination={tablePagination} />
        </Card>}
    </div>
}